import {MarketPlaceTradeStatus as TradeStatus} from '@afriex/core/domain/enums';

import type {MarketPlaceTradeSummary} from '@/types/MarketPlace';

import {
  formatAmount,
  formatCount,
  getTradeStatusClassName,
  getTradeStatusLabel,
} from './utils/format';

type TradeSummaryCardsProps = {
  summary: MarketPlaceTradeSummary;
  /** The applied status filter, if any. */
  status?: TradeStatus;
};

const Stat = ({label, value}: {label: string; value: string}) => (
  <div className="flex items-baseline gap-2">
    <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
      {label}
    </span>
    <span className="text-lg font-semibold tabular-nums text-gray-800 dark:text-gray-100">
      {value}
    </span>
  </div>
);

const STATUS_ORDER: TradeStatus[] = [
  TradeStatus.SETTLING,
  TradeStatus.SETTLED,
  TradeStatus.EXPIRED,
];

const TradeSummaryCards = ({summary, status}: TradeSummaryCardsProps) => {
  // The summary is filtered by the same status as the list, so the other two
  // counts collapse to zero. Showing them beside "Showing settled trades"
  // reads as a bug; a single line is honest.
  const showStatusBreakdown = !status;
  const showSettledByPair = !status || status === TradeStatus.SETTLED;

  return (
    <div className="mt-5 rounded-lg border border-gray-200 bg-gray-50 dark:border-dark-borderMid dark:bg-dark-inputBg">
      <div className="flex flex-wrap items-center gap-x-8 gap-y-2 px-4 py-3">
        <Stat label="Trades" value={formatCount(summary.tradeCount)} />
        <Stat label="Offers" value={formatCount(summary.offerCount)} />
        <Stat
          label="Counterparties"
          value={formatCount(summary.counterpartyCount)}
        />
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-gray-200 px-4 py-2 text-sm dark:border-dark-borderMid">
        {showStatusBreakdown ? (
          STATUS_ORDER.map((tradeStatus) => (
            <span
              key={tradeStatus}
              className={`font-medium ${getTradeStatusClassName(tradeStatus)}`}
            >
              {getTradeStatusLabel(tradeStatus)}:{' '}
              {formatCount(summary.statusCounts?.[tradeStatus] ?? 0)}
            </span>
          ))
        ) : (
          <span className="text-gray-600 dark:text-gray-300">
            Showing {formatCount(summary.tradeCount)}{' '}
            {getTradeStatusLabel(status).toLowerCase()} trades
          </span>
        )}

        {showSettledByPair && (
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-600 dark:text-gray-300">
            <span className="text-gray-500 dark:text-gray-400">
              Settled volume:
            </span>
            {summary.settledByPair.length === 0 ? (
              <span>none in this view</span>
            ) : (
              summary.settledByPair.map((entry, index) => (
                <span
                  key={`${entry.volume.asset}-${index}`}
                  className="flex items-center gap-2 tabular-nums"
                >
                  {index > 0 && (
                    <span aria-hidden="true" className="text-gray-400">
                      &middot;
                    </span>
                  )}
                  {formatAmount(entry.volume)} &rarr;{' '}
                  {formatAmount(entry.counterAmount)}
                </span>
              ))
            )}
          </span>
        )}
      </div>
    </div>
  );
};

export default TradeSummaryCards;
